/**
 * RdButton Component
 * 
 * A reusable button component with consistent styling for Room Designer headers and action bars
 */

import React from 'react';
import { Button, ButtonProps } from '@mui/material';
import { styled } from '@mui/material/styles';

export interface RdButtonProps extends ButtonProps {
  buttonType?: 'primary';
  startIcon?: React.ReactNode;
  children?: React.ReactNode;
}

const RdButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'buttonType',
})<RdButtonProps>(({ buttonType, theme }) => ({
  height: '32px',
  padding: '0 16px',
  borderRadius: '4px', // Updated from 6px to 4px per spec
  border: buttonType === 'primary'
    ? 'none !important'
    : `0.6px solid ${(theme as any).components?.button?.secondaryBorder || 'rgba(17, 17, 17, 0.40)'} !important`, // Use theme secondaryBorder
  backgroundColor: `${buttonType === 'primary' ? theme.palette.primary.main : theme.palette.background.default} !important`,
  color: `${buttonType === 'primary' ? theme.palette.primary.contrastText : theme.palette.text.primary} !important`,
  fontFamily: '"Open Sans"',
  fontWeight: 600,
  fontSize: '12px',
  fontStyle: 'normal',
  lineHeight: 'normal',
  textTransform: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  boxShadow: '0 1px 1px 0 rgba(0, 0, 0, 0.05)', // Added box-shadow per spec
  '& .MuiButton-startIcon': {
    margin: 0,
    display: 'flex',
    alignItems: 'center',
  },
  '&:hover': {
    backgroundColor: buttonType === 'primary'
      ? `${theme.palette.primary.dark} !important`
      : theme.palette.mode === 'light'
        ? 'rgba(0, 0, 0, 0.08) !important'
        : 'rgba(255, 255, 255, 0.12) !important',
    border: buttonType === 'primary'
      ? 'none !important'
      : `0.6px solid ${(theme as any).components?.button?.secondaryBorder || 'rgba(17, 17, 17, 0.40)'} !important`, // Keep consistent border on hover
    transform: 'translateY(-1px)',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  '&:disabled': {
    backgroundColor: buttonType === 'primary'
      ? `${theme.palette.primary.main}33 !important` // 20% opacity of primary color
      : `${theme.palette.action.disabledBackground} !important`,
    color: buttonType === 'primary'
      ? `${theme.palette.primary.main}99 !important` // 60% opacity of primary color
      : `${theme.palette.text.disabled} !important`,
    border: buttonType === 'primary'
      ? `1px solid ${theme.palette.primary.main}1A !important` // 10% opacity border
      : theme.palette.mode === 'light'
        ? `0.6px solid rgba(17, 17, 17, 0.20) !important` // Lighter border for disabled in light mode
        : `0.6px solid rgba(255, 255, 255, 0.20) !important`, // Lighter border for disabled in dark mode
    cursor: 'not-allowed',
    transform: 'none',
    boxShadow: 'none',
    opacity: buttonType === 'primary' ? 1 : 0.6,
    '&:hover': {
      backgroundColor: buttonType === 'primary'
        ? `${theme.palette.primary.main}33 !important`
        : `${theme.palette.action.disabledBackground} !important`,
      transform: 'none',
      boxShadow: 'none',
    },
  },
}));

export default RdButton;